import {Reducer, useCallback, useReducer} from 'react';
import {ReducerAction, ReducerActionWithData} from 'app/types/common';
import {BaseBilling, BaseBillingApi} from 'app/components/entities/billing/base';

export type CouponState = {code: string} & (
  | {type: 'empty'}
  | {type: 'typing'}
  | {type: 'checking'}
  | {type: 'valid'; instance: BaseBilling.Coupon}
  | {type: 'invalid'; error: BaseBilling.InvalidCoupon}
);

type Action =
  | ReducerAction<'empty'>
  | ReducerActionWithData<'typing', string>
  | ReducerAction<'checking'>
  | ReducerActionWithData<'valid', BaseBilling.Coupon>
  | ReducerActionWithData<'invalid', BaseBilling.InvalidCoupon>;

function reducer(state: CouponState, action: Action): CouponState {
  switch (action.type) {
    case 'empty': {
      return {type: 'empty', code: ''};
    }

    case 'typing': {
      return {type: 'typing', code: action.payload};
    }

    case 'checking': {
      return {type: 'checking', code: state.code};
    }

    case 'valid': {
      const {payload} = action;
      return {type: 'valid', instance: payload, code: state.code};
    }

    case 'invalid': {
      return {
        type: 'invalid',
        code: state.code,
        error: action.payload,
      };
    }

    default:
      return state;
  }
}

interface Return {
  coupon: CouponState;
  setCoupon: (value: string) => void;
  validateCoupon: (value: string) => Promise<void>;
}

export function useCoupon(teamId: string): Return {
  const [state, dispatch] = useReducer<Reducer<CouponState, Action>>(reducer, {
    type: 'typing',
    code: '',
  });

  const validate = useCallback(
    async (value: string) => {
      try {
        dispatch({type: 'checking'});
        const result = await BaseBillingApi.validateCoupon(teamId, value);
        if (BaseBilling.isInvalidCoupon(result)) {
          dispatch({type: 'invalid', payload: result});
        } else {
          dispatch({type: 'valid', payload: result});
        }
      } catch {
        dispatch({type: 'invalid', payload: {error: 'not_found'}});
      }
    },
    [teamId],
  );

  const setCoupon = useCallback((value: string) => {
    if (value === '') {
      dispatch({type: 'empty'});
      return;
    }
    dispatch({type: 'typing', payload: value});
  }, []);

  return {coupon: state, setCoupon, validateCoupon: validate};
}
